import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import { useQuery } from "@tanstack/react-query";
import { api, fillColor } from "@/lib/api";

export const useElectorates = () =>
  useQuery({
    queryKey: ["electorates"],
    queryFn: () => api.get("/electorates").then((r) => r.data),
    refetchInterval: 30000,
  });

const useGeo = () =>
  useQuery({
    queryKey: ["geojson"],
    queryFn: () => fetch("/data/electorates.geojson").then((r) => r.json()),
    staleTime: Infinity,
  });

const styleFor = (count, selected, dimmed = false) => ({
  fillColor: fillColor(count),
  fillOpacity: dimmed ? 0.15 : selected ? 0.9 : 0.75,
  color: selected ? "#E2705A" : dimmed ? "#8A97A6" : "#1B2A41",
  weight: selected ? 3 : dimmed ? 0.5 : 1.2,
  opacity: dimmed ? 0.5 : 0.95,
});

function FlyTo({ target, stateFilter, layersRef, geo }) {
  const map = useMap();
  useEffect(() => {
    const layer = target && layersRef.current[target];
    if (layer) map.flyToBounds(layer.getBounds(), { padding: [30, 30], maxZoom: 9, duration: 0.8 });
  }, [target, map, layersRef]);
  useEffect(() => {
    if (!geo) return;
    if (!stateFilter) {
      map.flyTo([-27.5, 134], 4, { duration: 0.8 });
      return;
    }
    let bounds = null;
    Object.values(layersRef.current).forEach((l) => {
      if (l.feature.properties.state === stateFilter) bounds = bounds ? bounds.extend(l.getBounds()) : l.getBounds();
    });
    if (bounds) map.flyToBounds(bounds, { padding: [20, 20], duration: 0.8 });
  }, [stateFilter, map, layersRef, geo]);
  return null;
}

export const ElectorateMap = ({ selected, onSelect, flyTarget, stateFilter }) => {
  const { data: geo } = useGeo();
  const { data: stats = [] } = useElectorates();
  const statsRef = useRef({});
  const layersRef = useRef({});
  const selectedRef = useRef(selected);
  const filterRef = useRef(stateFilter);

  statsRef.current = Object.fromEntries(stats.map((s) => [s.name, s]));
  selectedRef.current = selected;
  filterRef.current = stateFilter;

  const dimmed = (feature) => !!filterRef.current && feature.properties.state !== filterRef.current;

  useEffect(() => {
    Object.entries(layersRef.current).forEach(([name, layer]) => {
      layer.setStyle(styleFor(statsRef.current[name]?.count || 0, name === selected, dimmed(layer.feature)));
      if (name === selected) layer.bringToFront();
    });
  }, [stats, selected, stateFilter]);

  const onEach = (feature, layer) => {
    const name = feature.properties.electorateName;
    layersRef.current[name] = layer;
    layer.setStyle(styleFor(statsRef.current[name]?.count || 0, name === selectedRef.current, dimmed(feature)));
    layer.bindTooltip(
      () => {
        const s = statsRef.current[name] || { count: 0, groups: [] };
        return `<strong>${name}</strong> <span style="opacity:.7">(${feature.properties.state})</span><br/>${s.count} F.E.R.M${s.count === 1 ? "" : "s"} · ${s.groups.length} group${s.groups.length === 1 ? "" : "s"}`;
      },
      { sticky: true, className: "roan-tip", direction: "top" }
    );
    layer.on({
      click: () => onSelect(name),
      mouseover: () => {
        layer.setStyle({ weight: 2.5, fillOpacity: 0.9 });
        layer.bringToFront();
      },
      mouseout: () => layer.setStyle(styleFor(statsRef.current[name]?.count || 0, name === selectedRef.current, dimmed(feature))),
    });
  };

  return (
    <div
      data-testid="interactive-electorates-map"
      className="h-[460px] sm:h-[620px] w-full rounded-2xl border-2 border-[#1B2A41]/20 overflow-hidden shadow-inner relative z-0"
    >
      <MapContainer center={[-27.5, 134]} zoom={4} minZoom={3} maxZoom={12} scrollWheelZoom className="h-full w-full">
        <TileLayer
          attribution="Tiles &copy; Esri · Boundaries &copy; AEC"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
          maxZoom={16}
        />
        {geo && <GeoJSON data={geo} onEachFeature={onEach} />}
        <FlyTo target={flyTarget} stateFilter={stateFilter} layersRef={layersRef} geo={geo} />
      </MapContainer>
      <MapLegend />
    </div>
  );
};

const MapLegend = () => (
  <div className="absolute bottom-3 left-3 z-[400] bg-[#FAF8F2]/95 backdrop-blur rounded-xl border border-[#D8D2C4] px-3 py-2 text-[11px] text-[#1E2830] shadow" data-testid="map-legend">
    <p className="font-type font-bold uppercase tracking-wider mb-1.5 text-[#1B2A41]">F.E.R.Ms per electorate</p>
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {[
        [0, "0"],
        [2, "1–3"],
        [5, "4–6"],
        [8, "7–9"],
        [10, "10 full"],
        [11, "Group 2+"],
      ].map(([c, l]) => (
        <span key={l} className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm border border-[#1B2A41]/30" style={{ background: fillColor(c) }} />
          {l}
        </span>
      ))}
    </div>
  </div>
);
